import clsx from "clsx";
import { useEffect, useRef, useState } from "react";
import { CSSTransition } from "react-transition-group";
import { useKeyDown } from "../../utils/hooks/keydown";
import BackDrop from "../BackDrop";
import type { CardProps } from "../Card";
import Card from "../Card";
import styles from "./Modal.module.scss";

export type ModalProps = CardProps & {
  isOpen: boolean;
  open(): void;
  close(): void;
  onOpen?: (cb: (...args: any[]) => any) => void;
  onClose?: (cb: (...args: any[]) => any) => void;
  fullscreen?: boolean;
  fullscreenOnMobile?: boolean;
  bottomSheetOnMobile?: boolean;
};

export const useModal = () => {
  const [open, setOpen] = useState(false);

  const onCloseCB = useRef<(...args: any[]) => any>();
  const onOpenCB = useRef<(...args: any[]) => any>();

  return {
    isOpen: open,
    open() {
      setOpen(true);
      onOpenCB.current?.call(onOpenCB.current);
    },
    close() {
      setOpen(false);
      onCloseCB.current?.call(onOpenCB.current);
    },
    onOpen(cb: (...args: any[]) => any) {
      onOpenCB.current = cb;
    },
    onClose(cb: (...args: any[]) => any) {
      onCloseCB.current = cb;
    },
  };
};

const Modal = (props: ModalProps) => {
  const {
    fullscreen,
    fullscreenOnMobile,
    bottomSheetOnMobile,
    isOpen,
    open,
    close,
    ...cardProps
  } = props;

  useKeyDown(close, { code: "Escape" });

  useEffect(() => {
    document.body.style.overflow = isOpen ? "hidden" : "initial";

    return () => {
      document.body.style.overflow = "initial";
    };
  }, [isOpen]);

  return (
    <BackDrop open={isOpen} onClick={close}>
      <CSSTransition
        in={isOpen}
        unmountOnExit
        classNames={{
          enterDone: styles["modal--enter-done"],
          exit: styles["modal--exit"],
        }}
        timeout={{ enter: 0, exit: 300 }}
        appear
      >
        <Card
          {...cardProps}
          className={clsx(
            cardProps.className,
            styles["modal"],
            fullscreen && styles["modal--fullscreen"],
            fullscreenOnMobile && styles["modal--fullscreen-on-mobile"],
            bottomSheetOnMobile && styles["modal--bottomsheet-on-mobile"],
          )}
          onClick={(e) => e.stopPropagation()}
        />
      </CSSTransition>
    </BackDrop>
  );
};

export default Modal;
